import axios from 'axios'
import { clearToken, getToken, getRefreshToken, setToken } from './auth'
import { getClientSessionId } from '../telemetry/session'
import { getCurrentFlow } from '../telemetry/flow'
import { getClientRequestId } from '../telemetry/request'

// Browser client base URL contract:
// - VITE_API_BASE_URL: explicit browser-visible API origin (e.g. http://192.168.0.221:8082)
// - otherwise: same-origin relative base so Vite proxy (/api) or ingress handles routing.
//
// Note: VITE_API_BASE is intentionally NOT used by the browser client because it is often
// a Docker-internal host (e.g. http://web:8082) meant for the Vite proxy target only.
const envApiBase = String(import.meta.env.VITE_API_BASE_URL || '').trim()
export const API_BASE_URL = envApiBase.replace(/\/+$/, '')
const baseURL = API_BASE_URL || '/'

export const api = axios.create({
  baseURL,
  withCredentials: false,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

export const publicApi = axios.create({
  baseURL,
  withCredentials: false,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

function attachTelemetryHeaders(config: any) {
  config.headers = config.headers ?? {}
  const headers = config.headers as Record<string, string>
  if (!headers['X-Client-Session-Id']) headers['X-Client-Session-Id'] = getClientSessionId()
  if (!headers['X-Client-Request-Id']) headers['X-Client-Request-Id'] = getClientRequestId()
  const flow = getCurrentFlow()
  if (flow?.flowId && !headers['X-Client-Flow-Id']) headers['X-Client-Flow-Id'] = flow.flowId
  if (flow?.action && !headers['X-Client-Action']) headers['X-Client-Action'] = flow.action
  return config
}

api.interceptors.request.use((config) => {
  attachTelemetryHeaders(config)
  const token = getToken()
  if (token) {
    config.headers = config.headers ?? {}
    ;(config.headers as any).Authorization = `Bearer ${token}`
  }
  return config
})

publicApi.interceptors.request.use((config) => {
  attachTelemetryHeaders(config)
  return config
})

// 401 -> try refresh once using refresh token; then replay original
let isRefreshing = false
let waitQueue: Array<() => void> = []

async function refreshAccess(): Promise<string> {
  const refresh = getRefreshToken()
  if (!refresh) throw new Error('Missing refresh token')
  const { data } = await api.post('/api/auth/refresh/', { refresh })
  if (!data?.access) throw new Error('Refresh failed')
  setToken(data.access)
  return data.access
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status
    const original = err?.config ?? {}
    if (status === 401 && !original._retry) {
      original._retry = true
      try {
        if (isRefreshing) {
          await new Promise<void>((r) => waitQueue.push(r))
        } else {
          isRefreshing = true
          await refreshAccess()
          isRefreshing = false
          waitQueue.splice(0).forEach((fn) => fn())
        }
        // replay with new token
        original.headers = original.headers ?? {}
        const t = getToken()
        if (t) original.headers.Authorization = `Bearer ${t}`
        return api(original)
      } catch (e) {
        isRefreshing = false
        waitQueue.splice(0).forEach((fn) => fn())
        // fall through to normal error
      }
    }
    // If we reach here and still 401, clear tokens so UI can prompt login
    if (status === 401) {
      clearToken()
    }
    return Promise.reject(err)
  }
)
